import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Target } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ProcessStep {
  id: string;
  number: string;
  title: string;
  description: string;
  icon_name: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
}

const ICON_OPTIONS = [
  'Target', 'Briefcase', 'Rocket', 'Award', 'CheckCircle',
  'Search', 'PenTool', 'Code', 'Shield', 'Star'
];

export default function ProcessStepsManager() {
  const [steps, setSteps] = useState<ProcessStep[]>([]);
  const [editingStep, setEditingStep] = useState<ProcessStep | null>(null);
  const [formData, setFormData] = useState({
    number: '01',
    title: '',
    description: '',
    icon_name: 'Target',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchSteps();
  }, []);

  const fetchSteps = async () => {
    const { data, error } = await supabase
      .from('process_steps')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching process steps:', error);
    } else {
      setSteps(data || []);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingStep) {
      const { error } = await supabase
        .from('process_steps')
        .update(formData)
        .eq('id', editingStep.id);

      if (error) {
        console.error('Error updating process step:', error);
      } else {
        fetchSteps();
        resetForm();
      }
    } else {
      const { error } = await supabase
        .from('process_steps')
        .insert([formData]);

      if (error) {
        console.error('Error creating process step:', error);
      } else {
        fetchSteps();
        resetForm();
      }
    }
  };

  const handleEdit = (step: ProcessStep) => {
    setEditingStep(step);
    setFormData({
      number: step.number,
      title: step.title,
      description: step.description,
      icon_name: step.icon_name,
      display_order: step.display_order,
      is_active: step.is_active
    });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this process step?')) {
      const { error } = await supabase
        .from('process_steps')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting process step:', error);
      } else {
        fetchSteps();
      }
    }
  };

  const resetForm = () => {
    setFormData({
      number: '01',
      title: '',
      description: '',
      icon_name: 'Target',
      display_order: 0,
      is_active: true
    });
    setEditingStep(null);
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Process Steps Manager</h1>
        <p className="text-gray-600">Manage process steps displayed on the homepage</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingStep ? 'Edit Process Step' : 'Add New Step'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Step Number *
                </label>
                <input
                  type="text"
                  required
                  value={formData.number}
                  onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="01, 02, 03..."
                  maxLength={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Step Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Discovery & Strategy"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Describe this step in the process..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icon *
                </label>
                <select
                  value={formData.icon_name}
                  onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {ICON_OPTIONS.map((icon) => (
                    <option key={icon} value={icon}>
                      {icon}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  Active
                </label>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 inline-flex justify-center items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingStep ? 'Update' : 'Create'}
                </button>
                {editingStep && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Process Steps ({steps.length})</h2>
            </div>
            <div className="p-6">
              <div className="relative">
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-blue-200"></div>
                <div className="space-y-6">
                  {steps.map((step, index) => (
                    <div key={step.id} className="relative pl-16">
                      <div className="absolute left-0 top-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg z-10">
                        {step.number}
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6 ml-4 hover:shadow-lg transition-all">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                            <p className="text-gray-700 leading-relaxed">{step.description}</p>
                            <div className="flex items-center gap-2 mt-3">
                              <span className="text-sm text-gray-500">Order: {step.display_order}</span>
                              <span className="text-sm text-gray-500">Icon: {step.icon_name}</span>
                              {!step.is_active && (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                  Inactive
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 ml-4">
                            <button
                              onClick={() => handleEdit(step)}
                              className="p-2 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(step.id)}
                              className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {steps.length === 0 && (
                <div className="p-12 text-center text-gray-500">
                  No process steps found. Add your first step to get started.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
